import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type {
  BillingAuditTimeEntry,
  InvoiceGenerationSource,
  InvoicePreview,
  TimeEntryOverageStatus,
} from '@catalyst-psa/shared';
import { AGREEMENT_TYPE_LABELS, INVOICE_GENERATION_SOURCES } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Modal } from '../../components/ui/Modal.js';
import { formatDate, formatDateTime } from '../../utils/date.js';
import { CustomerSelectField } from '../customers/CustomerSelectField.js';
import {
  useGenerateInvoices,
  usePreviewInvoiceEntries,
  usePreviewInvoices,
  useRecomputeEntryRates,
  useWriteOffTimeEntry,
} from './api.js';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/**
 * Invoices are generated in small sequential batches (one request per batch) so a big
 * run never exceeds the Azure gateway request timeout (~230s). Each request only builds
 * plans for its own keys, keeping every request fast.
 */
const GENERATE_BATCH_SIZE = 5;

/** Badge for a managed-services hour's included / overage / split allocation. */
function AllocationBadge({ status }: { status: TimeEntryOverageStatus | null }): JSX.Element {
  if (status === 'overage') return <Badge tone="amber">Overage</Badge>;
  if (status === 'split') return <Badge tone="blue">Included + overage</Badge>;
  if (status === 'included') return <Badge tone="green">Included</Badge>;
  return <span className="text-slate-400">—</span>;
}

/** ISO date (yyyy-mm-dd) for a given day of the current month. */
function currentMonthDay(day: number): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), day))
    .toISOString()
    .slice(0, 10);
}

/**
 * Step the billing month by `delta` months relative to the current thru date:
 * thru date lands on the 1st of that month and the invoice date on the 10th.
 */
function shiftBillingMonthDates(
  thruDate: string,
  delta: number,
): { thruDate: string; invoiceDate: string } {
  const base = /^\d{4}-\d{2}-\d{2}$/.test(thruDate) ? new Date(`${thruDate}T00:00:00Z`) : new Date();
  const year = base.getUTCFullYear();
  const month = base.getUTCMonth() + delta;
  return {
    thruDate: new Date(Date.UTC(year, month, 1)).toISOString().slice(0, 10),
    invoiceDate: new Date(Date.UTC(year, month, 10)).toISOString().slice(0, 10),
  };
}

/**
 * Read-only billable-time table for the entries that would land on a selected project,
 * tickets, or managed-services invoice. Mirrors the columns of the Billing Audit
 * billable-time view; for managed services it also flags each hour included vs overage.
 */
function PreviewEntriesModal({
  source,
  preview,
  thruDate,
  fromDate,
  includeBilledExternally,
  canWriteOff,
  onClose,
}: {
  source: InvoiceGenerationSource;
  preview: InvoicePreview | null;
  thruDate: string;
  fromDate: string;
  includeBilledExternally: boolean;
  canWriteOff: boolean;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const query = usePreviewInvoiceEntries(
    source,
    preview?.key ?? null,
    thruDate,
    fromDate,
    includeBilledExternally,
    preview !== null,
  );
  const writeOff = useWriteOffTimeEntry();
  const recompute = useRecomputeEntryRates();
  const entries = query.data ?? [];
  const totalHours = entries.reduce((sum, e) => sum + Number(e.hours), 0);
  // Managed-services entries carry an included/overage allocation; show it as a column.
  const showAllocation = source === 'managed_services';
  // On the tickets tab, the reference column shows the ticket ID (ClickUp custom task ID)
  // instead of the ConnectWise time-entry RecID.
  const isTickets = source === 'tickets';
  const baseColumns = canWriteOff ? 14 : 13;
  const colSpan = showAllocation ? baseColumns + 1 : baseColumns;

  // Writing off an entry removes it from the preview totals, so refresh the entire
  // invoices cache (both the preview list and this entry list).
  const handleWriteOff = (id: string): void => {
    writeOff.mutate(
      { timeEntryId: id, input: { writtenOff: true } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: ['invoices'] });
        },
      },
    );
  };

  // Re-resolve rates for exactly the entries in this list (picks up phase/project rate
  // changes made after the hours were logged), then refresh the invoices cache.
  const handleRecompute = (): void => {
    const ids = entries.map((e) => e.id);
    if (ids.length === 0) return;
    recompute.mutate(ids, {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      },
    });
  };

  return (
    <Modal
      isOpen={preview !== null}
      onClose={onClose}
      title={
        preview
          ? `Time entries · ${preview.projectName ?? preview.agreementName ?? preview.customerName}`
          : 'Time entries'
      }
      size="wide"
      fillHeight
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="overflow-x-auto">
        <div className="mb-2 flex items-center justify-between gap-3">
          <p className="text-sm text-slate-500">
            {entries.length} entr{entries.length === 1 ? 'y' : 'ies'} · {totalHours.toFixed(2)}{' '}
            hours billable as of {thruDate}.
          </p>
          {canWriteOff ? (
            <Button
              variant="secondary"
              onClick={handleRecompute}
              disabled={entries.length === 0}
              isLoading={recompute.isPending}
            >
              Recompute rates
            </Button>
          ) : null}
        </div>
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 font-medium">Work date</th>
              <th className="px-3 py-2 font-medium">Member</th>
              <th className="px-3 py-2 font-medium">Customer</th>
              <th className="px-3 py-2 font-medium">Agreement</th>
              <th className="px-3 py-2 font-medium">Project</th>
              <th className="px-3 py-2 font-medium">Ticket</th>
              <th className="px-3 py-2 font-medium">{isTickets ? 'Ticket ID' : 'CW ID'}</th>
              <th className="px-3 py-2 font-medium">Hours</th>
              {showAllocation ? <th className="px-3 py-2 font-medium">Allocation</th> : null}
              <th className="px-3 py-2 font-medium">Rate</th>
              <th className="px-3 py-2 font-medium">Billable</th>
              <th className="px-3 py-2 font-medium">Invoice</th>
              <th className="px-3 py-2 font-medium">Invoiced</th>
              <th className="px-3 py-2 font-medium">Billed ext.</th>
              {canWriteOff ? <th className="px-3 py-2 font-medium">Actions</th> : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={colSpan}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={entries.length === 0}
              emptyTitle="No time entries"
              emptyDescription="No billable time entries were found for this invoice as of the Thru Date."
            >
              {entries.map((entry: BillingAuditTimeEntry) => (
                <tr key={entry.id} className="align-top">
                  <td className="px-3 py-2 tabular-nums text-slate-600">
                    {formatDate(entry.workDate)}
                  </td>
                  <td className="px-3 py-2 text-slate-900">{entry.memberName}</td>
                  <td className="px-3 py-2 text-slate-600">{entry.customerName ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{entry.agreementName ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{entry.projectName ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{entry.ticketTitle ?? '—'}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">
                    {isTickets ? (entry.ticketReferenceId ?? '—') : (entry.cwId ?? '—')}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">{entry.hours}</td>
                  {showAllocation ? (
                    <td className="px-3 py-2">
                      <AllocationBadge status={entry.overageStatus ?? null} />
                    </td>
                  ) : null}
                  <td className="px-3 py-2 tabular-nums text-slate-600">{entry.resolvedRate}</td>
                  <td className="px-3 py-2 text-slate-600">{entry.billable ? 'Yes' : 'No'}</td>
                  <td className="px-3 py-2 text-slate-600">{entry.invoiceNumber ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{formatDateTime(entry.invoicedAt)}</td>
                  <td className="px-3 py-2 text-slate-600">
                    {entry.billedExternally ? 'External' : '—'}
                  </td>
                  {canWriteOff ? (
                    <td className="px-3 py-2">
                      <Button
                        variant="secondary"
                        onClick={() => handleWriteOff(entry.id)}
                        isLoading={
                          writeOff.isPending && writeOff.variables?.timeEntryId === entry.id
                        }
                      >
                        Write off
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

const TAB_LABELS: Record<InvoiceGenerationSource, string> = {
  product_subscription: AGREEMENT_TYPE_LABELS.product_subscription,
  managed_services: AGREEMENT_TYPE_LABELS.managed_services,
  project: AGREEMENT_TYPE_LABELS.project,
  tickets: AGREEMENT_TYPE_LABELS.tickets,
};

interface GenerationTabProps {
  source: InvoiceGenerationSource;
  thruDate: string;
  fromDate: string;
  invoiceDate: string;
  customerId: string;
  includeBilledExternally: boolean;
  showQboClass: boolean;
  showCadence: boolean;
  canEdit: boolean;
}

/** Columns of the invoice-generation preview table that can be sorted. */
type PreviewSortKey = 'customer' | 'source' | 'period' | 'lines' | 'total';

type PreviewSort = { key: PreviewSortKey; dir: 'asc' | 'desc' };

/** Comparable value for a preview row under a given sort column. */
function previewSortValue(p: InvoicePreview, key: PreviewSortKey): string | number {
  switch (key) {
    case 'customer':
      return p.customerName?.toLowerCase() ?? '';
    case 'source':
      return (p.agreementName ?? p.projectName ?? '').toLowerCase();
    case 'period':
      return p.periodStart ?? '';
    case 'lines':
      return p.lines.length;
    case 'total':
      return Number(p.total);
  }
}

/** Clickable column header that toggles sorting for the preview table. */
function SortableHeader({
  label,
  sortKey,
  sort,
  onToggle,
  align = 'left',
}: {
  label: string;
  sortKey: PreviewSortKey;
  sort: PreviewSort | null;
  onToggle: (key: PreviewSortKey) => void;
  align?: 'left' | 'right';
}): JSX.Element {
  const active = sort?.key === sortKey;
  const indicator = active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕';
  return (
    <th className={`px-4 py-3 font-medium ${align === 'right' ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide ${
          align === 'right' ? 'flex-row-reverse' : ''
        } ${active ? 'text-slate-700' : 'hover:text-slate-700'}`}
        aria-label={`Sort by ${label}`}
      >
        {label}
        <span className={`text-[10px] ${active ? 'text-slate-700' : 'text-slate-300'}`}>
          {indicator}
        </span>
      </button>
    </th>
  );
}

function GenerationTab({
  source,
  thruDate,
  fromDate,
  invoiceDate,
  customerId,
  includeBilledExternally,
  showQboClass,
  showCadence,
  canEdit,
}: GenerationTabProps): JSX.Element {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<PreviewSort | null>(null);
  // For project/tickets sources, clicking a row opens its underlying time entries.
  const [entriesPreview, setEntriesPreview] = useState<InvoicePreview | null>(null);
  const rowsClickable =
    source === 'project' || source === 'tickets' || source === 'managed_services';

  const previewQuery = usePreviewInvoices(
    source,
    thruDate,
    fromDate,
    includeBilledExternally,
    true,
  );
  const generateMutation = useGenerateInvoices();

  const allPreviews = previewQuery.data ?? [];
  // Customer filter is applied client-side so the cached preview is reused across
  // customer selections without refetching.
  const previews = customerId
    ? allPreviews.filter((p) => p.customerId === customerId)
    : allPreviews;

  // Running total of every (filtered) invoice's total on this tab.
  const totalSum = previews.reduce((sum, p) => sum + Number(p.total), 0);

  // Sorting only reorders the visible rows; selection membership is unchanged.
  const sortedPreviews = useMemo(() => {
    if (!sort) return previews;
    return [...previews].sort((a, b) => {
      const av = previewSortValue(a, sort.key);
      const bv = previewSortValue(b, sort.key);
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [previews, sort]);

  const toggleSort = (key: PreviewSortKey): void => {
    setSort((prev) =>
      prev && prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    );
  };

  const toggle = (key: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allSelected = previews.length > 0 && previews.every((p) => selected.has(p.key));
  const toggleAll = (): void => {
    setSelected(allSelected ? new Set() : new Set(previews.map((p) => p.key)));
  };

  // Only generate the currently-visible (filtered) selections.
  const selectedVisibleKeys = previews.filter((p) => selected.has(p.key)).map((p) => p.key);

  // Batched generation progress: null when idle. Keys that fail stay selected so the
  // user can retry just the failures.
  const [runProgress, setRunProgress] = useState<{ done: number; total: number } | null>(null);
  const [runSummary, setRunSummary] = useState<{ created: number; failed: number } | null>(null);
  const generating = runProgress !== null;

  const handleGenerate = async (): Promise<void> => {
    if (selectedVisibleKeys.length === 0 || generating) return;
    const keys = [...selectedVisibleKeys];
    setRunProgress({ done: 0, total: keys.length });
    setRunSummary(null);

    let created = 0;
    let failed = 0;
    for (let i = 0; i < keys.length; i += GENERATE_BATCH_SIZE) {
      const batch = keys.slice(i, i + GENERATE_BATCH_SIZE);
      try {
        const invoices = await generateMutation.mutateAsync({
          source,
          // Pass the SAME From Date + externally-billed option the preview used so the
          // created invoices match the preview exactly.
          input: {
            thruDate,
            invoiceDate,
            keys: batch,
            ...(fromDate ? { fromDate } : {}),
            includeBilledExternally,
          },
        });
        created += invoices.length;
        // Unselect only the keys that were actually created; failures stay selected.
        setSelected((prev) => {
          const next = new Set(prev);
          for (const key of batch) next.delete(key);
          return next;
        });
      } catch {
        failed += batch.length;
      }
      setRunProgress({ done: Math.min(i + batch.length, keys.length), total: keys.length });
    }

    setRunProgress(null);
    setRunSummary({ created, failed });
    void previewQuery.refetch();
  };

  return (
    <div>
      <div className="flex items-center justify-between border-b border-slate-200 p-4">
        <div className="text-sm text-slate-500">
          <p>
            {previews.length} invoice{previews.length === 1 ? '' : 's'} ready to create as of{' '}
            {thruDate}.
          </p>
          <p className="mt-0.5 text-slate-700">
            Total: <span className="font-semibold text-slate-900">{currency.format(totalSum)}</span>
          </p>
        </div>
        {canEdit ? (
          <Button
            onClick={() => void handleGenerate()}
            disabled={selectedVisibleKeys.length === 0 || generating}
            isLoading={generating}
          >
            {generating
              ? `Creating ${runProgress.done} of ${runProgress.total}…`
              : `Create ${
                  selectedVisibleKeys.length > 0 ? `${selectedVisibleKeys.length} ` : ''
                }invoice${selectedVisibleKeys.length === 1 ? '' : 's'}`}
          </Button>
        ) : null}
      </div>

      {runSummary && runSummary.failed > 0 ? (
        <div className="border-b border-slate-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
          Created {runSummary.created} invoice{runSummary.created === 1 ? '' : 's'};{' '}
          {runSummary.failed} failed and remain{runSummary.failed === 1 ? 's' : ''} selected.
          Press Create again to retry the remaining item{runSummary.failed === 1 ? '' : 's'}.
        </div>
      ) : null}
      {runSummary && runSummary.failed === 0 && runSummary.created > 0 ? (
        <div className="border-b border-slate-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
          Created {runSummary.created} invoice{runSummary.created === 1 ? '' : 's'}.
        </div>
      ) : null}

      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="w-10 px-4 py-3">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                disabled={previews.length === 0 || !canEdit}
                aria-label="Select all"
              />
            </th>
            <SortableHeader label="Customer" sortKey="customer" sort={sort} onToggle={toggleSort} />
            <SortableHeader label="Source" sortKey="source" sort={sort} onToggle={toggleSort} />
            <SortableHeader label="Period" sortKey="period" sort={sort} onToggle={toggleSort} />
            <SortableHeader label="Lines" sortKey="lines" sort={sort} onToggle={toggleSort} />
            <SortableHeader
              label="Total"
              sortKey="total"
              sort={sort}
              onToggle={toggleSort}
              align="right"
            />
            <th className="px-4 py-3 font-medium">Last invoice</th>
            <th className="px-4 py-3 font-medium">Billing reminder</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          <DataState
            colSpan={8}
            isLoading={previewQuery.isLoading}
            isError={previewQuery.isError}
            error={previewQuery.error}
            onRetry={() => void previewQuery.refetch()}
            isEmpty={previews.length === 0}
            emptyTitle="Nothing to invoice"
            emptyDescription="No billable items were found for this source as of the Thru Date."
          >
            {sortedPreviews.map((preview) => (
              <tr
                key={preview.key}
                className={`align-top hover:bg-slate-50 ${rowsClickable ? 'cursor-pointer' : ''}`}
                onClick={rowsClickable ? () => setEntriesPreview(preview) : undefined}
              >
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.has(preview.key)}
                    onChange={() => toggle(preview.key)}
                    disabled={!canEdit}
                    aria-label={`Select ${preview.customerName}`}
                  />
                </td>
                <td className="px-4 py-3 font-medium">
                  <Link
                    to={`/customers?returnCustomerId=${preview.customerId}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-brand-600 hover:underline"
                  >
                    {preview.customerName}
                  </Link>
                </td>
                <td className="px-4 py-3 text-slate-600">
                  {preview.agreementId ? (
                    <Link
                      to={`/agreements/${preview.agreementId}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-brand-600 hover:underline"
                    >
                      {preview.agreementName ?? '—'}
                    </Link>
                  ) : preview.projectId ? (
                    <Link
                      to={`/projects/${preview.projectId}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-brand-600 hover:underline"
                    >
                      {preview.projectName ?? '—'}
                    </Link>
                  ) : (
                    (preview.agreementName ?? preview.projectName ?? '—')
                  )}
                </td>
                <td className="px-4 py-3 text-slate-600">
                  {preview.periodStart && preview.periodEnd
                    ? `${formatDate(preview.periodStart)} → ${formatDate(preview.periodEnd)}`
                    : '—'}
                </td>
                <td className="px-4 py-3 text-slate-600">
                  <ul className="space-y-0.5">
                    {preview.lines.map((line, index) => (
                      <li key={index} className="text-xs">
                        {line.description} —{' '}
                        <span className="text-slate-500">
                          {line.quantity} × {currency.format(Number(line.unitRate))} ={' '}
                          {currency.format(Number(line.amount))}
                        </span>
                        {showQboClass ? (
                          line.qboClass ? (
                            <span className="ml-1 inline-flex items-center rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">
                              {line.qboClass}
                            </span>
                          ) : (
                            <span className="ml-1 inline-flex items-center rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-700">
                              No QBO class
                            </span>
                          )
                        ) : null}
                        {showCadence && line.billingFrequency ? (
                          <span className="ml-1 inline-flex items-center rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                            {line.billingFrequency === 'yearly' ? 'Yearly' : 'Monthly'}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </td>
                <td className="px-4 py-3 text-right font-medium text-slate-900">
                  {currency.format(Number(preview.total))}
                </td>
                <td className="px-4 py-3 text-slate-600">
                  {preview.lastInvoiceDate ? (
                    <span>
                      {formatDate(preview.lastInvoiceDate)}
                      <span className="block text-xs text-slate-400">
                        {currency.format(Number(preview.lastInvoiceAmount))}
                      </span>
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-600">{preview.billingReminder || '—'}</td>
              </tr>
            ))}
          </DataState>
        </tbody>
      </table>

      {rowsClickable ? (
        <PreviewEntriesModal
          source={source}
          preview={entriesPreview}
          thruDate={thruDate}
          fromDate={fromDate}
          includeBilledExternally={includeBilledExternally}
          canWriteOff={canEdit}
          onClose={() => setEntriesPreview(null)}
        />
      ) : null}
    </div>
  );
}

export function GenerateInvoicesPage(): JSX.Element {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canEdit = can('invoices', 'edit');

  const [thruDate, setThruDate] = useState(() => currentMonthDay(1));
  const [fromDate, setFromDate] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(() => currentMonthDay(10));
  const [customerId, setCustomerId] = useState('');
  const [includeBilledExternally, setIncludeBilledExternally] = useState(false);
  const [showQboClass, setShowQboClass] = useState(false);
  const [showCadence, setShowCadence] = useState(false);
  const [activeTab, setActiveTab] = useState<InvoiceGenerationSource>('product_subscription');

  // Fetch all four sources at the page level so the overall total + export cover every
  // tab. react-query dedupes by key, so the active tab's own query is shared (no double
  // fetch). Sources are a fixed-length constant, so these hooks are stable across renders.
  const subscriptionQuery = usePreviewInvoices(
    'product_subscription',
    thruDate,
    fromDate,
    includeBilledExternally,
    true,
  );
  const managedQuery = usePreviewInvoices(
    'managed_services',
    thruDate,
    fromDate,
    includeBilledExternally,
    true,
  );
  const projectQuery = usePreviewInvoices(
    'project',
    thruDate,
    fromDate,
    includeBilledExternally,
    true,
  );
  const ticketsQuery = usePreviewInvoices(
    'tickets',
    thruDate,
    fromDate,
    includeBilledExternally,
    true,
  );

  const allPreviews = useMemo(() => {
    const combined = [
      ...(subscriptionQuery.data ?? []),
      ...(managedQuery.data ?? []),
      ...(projectQuery.data ?? []),
      ...(ticketsQuery.data ?? []),
    ];
    return customerId ? combined.filter((p) => p.customerId === customerId) : combined;
  }, [subscriptionQuery.data, managedQuery.data, projectQuery.data, ticketsQuery.data, customerId]);

  // Per-tab totals + ready counts shown alongside the grand total (same customer filter).
  const sourceTotals = useMemo(() => {
    const summarize = (rows: InvoicePreview[] | undefined): { total: number; count: number } => {
      const filtered = (rows ?? []).filter((p) => !customerId || p.customerId === customerId);
      return {
        total: filtered.reduce((sum, p) => sum + Number(p.total), 0),
        count: filtered.length,
      };
    };
    return {
      product_subscription: summarize(subscriptionQuery.data),
      managed_services: summarize(managedQuery.data),
      project: summarize(projectQuery.data),
      tickets: summarize(ticketsQuery.data),
    } as Record<InvoiceGenerationSource, { total: number; count: number }>;
  }, [subscriptionQuery.data, managedQuery.data, projectQuery.data, ticketsQuery.data, customerId]);

  const overallTotal = allPreviews.reduce((sum, p) => sum + Number(p.total), 0);
  const anyLoading =
    subscriptionQuery.isLoading ||
    managedQuery.isLoading ||
    projectQuery.isLoading ||
    ticketsQuery.isLoading;

  const handleExport = (): void => {
    const csv = buildPreviewCsv(allPreviews);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`invoices-ready-to-create-${thruDate}-${stamp}.csv`, csv);
  };

  const shiftBillingMonth = (delta: number): void => {
    const next = shiftBillingMonthDates(thruDate, delta);
    setThruDate(next.thruDate);
    setInvoiceDate(next.invoiceDate);
  };

  return (
    <div>
      <PageHeader
        title="Generate Invoices"
        description="Generate invoices on demand from one of four sources, then manage them on the Invoices page."
        actions={
          <Button variant="secondary" onClick={() => navigate('/invoices')}>
            Manage invoices
          </Button>
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-end gap-4 border-b border-slate-200 p-4">
          <label className="flex flex-col text-sm">
            <span className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
              From date
            </span>
            <input
              type="date"
              value={fromDate}
              max={thruDate || undefined}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <span className="mt-1 text-xs text-slate-400">Blank = all history</span>
          </label>
          <div className="flex flex-col">
            <div className="mb-1 flex items-center gap-2">
              <button
                type="button"
                onClick={() => shiftBillingMonth(-1)}
                aria-label="Previous billing month"
                title="Previous month (thru date = 1st, invoice date = 10th)"
                className="rounded-lg border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12.5 4.5 7 10l5.5 5.5" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => shiftBillingMonth(1)}
                aria-label="Next billing month"
                title="Next month (thru date = 1st, invoice date = 10th)"
                className="rounded-lg border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 4.5 13 10l-5.5 5.5" />
                </svg>
              </button>
            </div>
            <div className="flex flex-wrap items-end gap-4">
              <label className="flex flex-col text-sm">
                <span className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                  Thru date
                </span>
                <input
                  type="date"
                  value={thruDate}
                  onChange={(e) => setThruDate(e.target.value)}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </label>
              <label className="flex flex-col text-sm">
                <span className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                  Invoice date
                </span>
                <input
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </label>
            </div>
          </div>
          <div className="flex w-64 flex-col text-sm">
            <span className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
              Customer
            </span>
            <CustomerSelectField
              value={customerId}
              onChange={setCustomerId}
              allowClear
              placeholder="All customers"
            />
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={includeBilledExternally}
              onChange={(e) => setIncludeBilledExternally(e.target.checked)}
            />
            <span>
              Include hours already billed in ConnectWise
              <span className="block text-xs text-slate-400">
                For parallel testing — shows externally-billed time in the previews.
              </span>
            </span>
          </label>
          <label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={showQboClass}
              onChange={(e) => setShowQboClass(e.target.checked)}
            />
            <span>Show QBO class</span>
          </label>
          <label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={showCadence}
              onChange={(e) => setShowCadence(e.target.checked)}
            />
            <span>Show monthly/yearly</span>
          </label>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm text-slate-600">
            {INVOICE_GENERATION_SOURCES.map((source) => (
              <span key={source}>
                {TAB_LABELS[source]}:{' '}
                <span className="font-medium tabular-nums text-slate-800">
                  {currency.format(sourceTotals[source].total)}
                </span>
                <span className="ml-1 text-xs text-slate-400">({sourceTotals[source].count})</span>
              </span>
            ))}
            <span className="border-l border-slate-300 pl-6">
              All four tabs total{customerId ? ' (this customer)' : ''}:{' '}
              <span className="font-semibold tabular-nums text-slate-900">
                {currency.format(overallTotal)}
              </span>
              {anyLoading ? <span className="text-slate-400"> · loading…</span> : null}
              <span className="ml-2 text-xs text-slate-400">
                {allPreviews.length} invoice{allPreviews.length === 1 ? '' : 's'} ready
              </span>
            </span>
          </div>
          <Button variant="secondary" onClick={handleExport} disabled={allPreviews.length === 0}>
            Export ready-to-create (CSV)
          </Button>
        </div>

        <div className="flex flex-wrap gap-1 border-b border-slate-200 px-4 pt-3">
          {INVOICE_GENERATION_SOURCES.map((source) => (
            <button
              key={source}
              type="button"
              onClick={() => setActiveTab(source)}
              className={`rounded-t-lg border-b-2 px-4 py-2 text-sm font-medium ${
                activeTab === source
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {TAB_LABELS[source]}
            </button>
          ))}
        </div>

        <GenerationTab
          key={activeTab}
          source={activeTab}
          thruDate={thruDate}
          fromDate={fromDate}
          invoiceDate={invoiceDate}
          customerId={customerId}
          includeBilledExternally={includeBilledExternally}
          showQboClass={showQboClass}
          showCadence={showCadence}
          canEdit={canEdit}
        />
      </div>
    </div>
  );
}

/** CSV field escaping per RFC 4180 (quote when it contains comma/quote/newline). */
function csvField(value: string | number | null | undefined): string {
  const s = value == null ? '' : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Flattens the ready-to-create previews across all four tabs into a line-level CSV with
 * every field that helps compare/triage against ConnectWise (customer, agreement/project,
 * period, per-line kind/description/quantity/rate/amount, and the invoice total).
 */
function buildPreviewCsv(previews: InvoicePreview[]): string {
  const header = [
    'source',
    'sourceLabel',
    'customerId',
    'customerName',
    'agreementId',
    'agreementName',
    'projectId',
    'projectName',
    'billingReminder',
    'periodStart',
    'periodEnd',
    'invoiceTotal',
    'lineNumber',
    'lineKind',
    'lineDescription',
    'lineQuantity',
    'lineUnitRate',
    'lineAmount',
    'productId',
  ];
  const rows: string[] = [header.map(csvField).join(',')];
  for (const p of previews) {
    const base = [
      p.source,
      TAB_LABELS[p.source],
      p.customerId,
      p.customerName,
      p.agreementId,
      p.agreementName,
      p.projectId,
      p.projectName,
      p.billingReminder,
      p.periodStart,
      p.periodEnd,
      p.total,
    ];
    if (p.lines.length === 0) {
      rows.push([...base, '', '', '', '', '', '', ''].map(csvField).join(','));
      continue;
    }
    p.lines.forEach((line, index) => {
      rows.push(
        [
          ...base,
          index + 1,
          line.kind,
          line.description,
          line.quantity,
          line.unitRate,
          line.amount,
          line.productId ?? '',
        ]
          .map(csvField)
          .join(','),
      );
    });
  }
  return rows.join('\r\n');
}

/** Triggers a browser download of a CSV string. */
function downloadCsv(fileName: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
